import React, { useState, useCallback, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "../../axios/axiosInstance";
import ConfirmModal from "components/ConfirmModal";

const companyId = () => localStorage.getItem("companyId") || "platform";

const api = {
    getYears:    ()           => axiosInstance.get(`/companies/${companyId()}/tax-brackets/years`).then(r => r.data),
    getBrackets: (year, jur)  => axiosInstance.get(`/companies/${companyId()}/tax-brackets?year=${year}&jurisdiction=${jur}`).then(r => r.data),
    update:      (id, body)   => axiosInstance.put(`/companies/${companyId()}/tax-brackets/${id}`, body).then(r => r.data),
    create:      (body)       => axiosInstance.post(`/companies/${companyId()}/tax-brackets`, body).then(r => r.data),
    remove:      (id)         => axiosInstance.delete(`/companies/${companyId()}/tax-brackets/${id}`),
    copyYear:    (body)       => axiosInstance.post(`/companies/${companyId()}/tax-brackets/copy-year`, body).then(r => r.data),
};

const sdiApi = {
    getAll:  (year)     => axiosInstance.get(`/admin/tax-flat-rates?taxType=SDI&year=${year}`).then(r => r.data),
    update:  (id, body) => axiosInstance.put(`/admin/tax-flat-rates/${id}`, body).then(r => r.data),
    create:  (body)     => axiosInstance.post(`/admin/tax-flat-rates`, body).then(r => r.data),
    remove:  (id)       => axiosInstance.delete(`/admin/tax-flat-rates/${id}`),
};

const localApi = {
    getAll:  (state, year) => axiosInstance.get(`/admin/local-tax-rates?state=${state}&year=${year}`).then(r => r.data),
    update:  (id, body)    => axiosInstance.put(`/admin/local-tax-rates/${id}`, body).then(r => r.data),
    create:  (body)        => axiosInstance.post(`/admin/local-tax-rates`, body).then(r => r.data),
    remove:  (id)          => axiosInstance.delete(`/admin/local-tax-rates/${id}`),
};

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];

const fmtRate = v => v != null ? (Number(v) * 100).toFixed(4) + "%" : "—";
const fmtAmt  = v => v != null ? Number(v).toLocaleString("en-US", { style: "currency", currency: "USD" }) : "—";

const inp = { fontSize: 12, border: "1px solid #d1d5db", borderRadius: 4, padding: "3px 6px" };
const sel = { ...inp, padding: "3px 24px 3px 6px", appearance: "auto", WebkitAppearance: "auto", minWidth: 70 };
const btn = { fontSize: 12, border: "none", borderRadius: 4, padding: "4px 10px", cursor: "pointer" };

const EditRow = ({ bracket, onSave, onCancel }) => {
    const [form, setForm] = useState({ ...bracket });
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
    return (
        <tr style={{ background: "#fffbeb" }}>
            <td style={{ padding: "4px 8px", fontFamily: "monospace", fontSize: 11 }}>{bracket.id}</td>
            <td><select value={form.filingStatus} onChange={e => set("filingStatus", e.target.value)} style={inp}><option>S</option><option>MFJ</option><option>MFS</option><option>HOH</option><option>ALL</option></select></td>
            <td><input value={form.minWage ?? ""} onChange={e => set("minWage", e.target.value)} style={{ ...inp, width: 90 }} /></td>
            <td><input value={form.maxWage ?? ""} onChange={e => set("maxWage", e.target.value || null)} style={{ ...inp, width: 90 }} placeholder="∞" /></td>
            <td><input value={form.baseAmount ?? ""} onChange={e => set("baseAmount", e.target.value)} style={{ ...inp, width: 90 }} /></td>
            <td><input value={form.rate ?? ""} onChange={e => set("rate", e.target.value)} style={{ ...inp, width: 90 }} placeholder="0.05 = 5%" /></td>
            <td><input value={form.excessOver ?? ""} onChange={e => set("excessOver", e.target.value)} style={{ ...inp, width: 90 }} /></td>
            <td>
                <button onClick={() => onSave(form)} style={{ ...btn, background: "#16a34a", color: "#fff", marginRight: 4 }}>Save</button>
                <button onClick={onCancel} style={{ ...btn, background: "#6b7280", color: "#fff" }}>Cancel</button>
            </td>
        </tr>
    );
};

const BracketTable = ({ jurisdiction, taxType, yearOverride }) => {
    const qc = useQueryClient();
    const [year, setYear]       = useState(null);
    const [copyFrom, setCopyFrom] = useState(null);
    const [copyTo, setCopyTo]   = useState(new Date().getFullYear() + 1);
    const [copyMsg, setCopyMsg] = useState("");
    const [editId, setEditId]   = useState(null);
    const [addRow, setAddRow]   = useState(false);
    const [newRow, setNewRow]   = useState({ year: null, jurisdiction, taxType, filingStatus: "S", minWage: 0, maxWage: null, baseAmount: 0, rate: 0, excessOver: 0 });
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);

    const { data: years = [] } = useQuery({ queryKey: ["tb-years"], queryFn: api.getYears });

    useEffect(() => {
        if (years.length > 0) {
            if (!year) setYear(yearOverride ?? years[0]);
            if (!copyFrom) setCopyFrom(yearOverride ?? years[0]);
        }
    }, [years]); // eslint-disable-line react-hooks/exhaustive-deps

    const { data: brackets = [], isLoading } = useQuery({
        queryKey: ["tb", year, jurisdiction],
        queryFn: () => api.getBrackets(year, jurisdiction),
        enabled: !!year,
    });

    const invalidate = useCallback(() => qc.invalidateQueries({ queryKey: ["tb", year, jurisdiction] }), [qc, year, jurisdiction]);

    const updateMut = useMutation({ mutationFn: ({ id, body }) => api.update(id, body), onSuccess: () => { setEditId(null); invalidate(); } });
    const createMut = useMutation({ mutationFn: api.create, onSuccess: () => { setAddRow(false); invalidate(); } });
    const deleteMut = useMutation({ mutationFn: api.remove, onSuccess: invalidate });
    const copyMut   = useMutation({
        mutationFn: () => api.copyYear({ fromYear: Number(copyFrom), toYear: Number(copyTo), overwrite: false }),
        onSuccess: r => { setCopyMsg(`Copied ${r.copied} brackets from ${copyFrom} → ${copyTo}.`); qc.invalidateQueries({ queryKey: ["tb-years"] }); },
        onError:   e => setCopyMsg(e?.response?.data || "Copy failed — year may already exist."),
    });

    const handleSave   = (id, form) => updateMut.mutate({ id, body: { ...form, rate: Number(form.rate), minWage: Number(form.minWage), maxWage: form.maxWage != null ? Number(form.maxWage) : null, baseAmount: Number(form.baseAmount), excessOver: Number(form.excessOver) } });
    const handleCreate = () => createMut.mutate({ ...newRow, year: Number(year), rate: Number(newRow.rate), minWage: Number(newRow.minWage), maxWage: newRow.maxWage != null ? Number(newRow.maxWage) : null, baseAmount: Number(newRow.baseAmount), excessOver: Number(newRow.excessOver) });

    return (
        <div>
            {/* Year Status Bar */}
            {year && (() => {
                const currentYear = new Date().getFullYear();
                const maxYear = years.length > 0 ? Math.max(...years) : null;
                const isLatest = year === maxYear;
                const isCurrent = year === currentYear;
                const isMissing = maxYear && maxYear < currentYear;
                return (
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
                        <div style={{
                            display: "flex", alignItems: "center", gap: 8,
                            background: "#1a3a5c", color: "#fff",
                            borderRadius: 8, padding: "10px 18px", fontSize: 13,
                        }}>
                            <span style={{ fontSize: 20 }}>📋</span>
                            <span>Viewing <strong>{year}</strong> tax brackets</span>
                            {isLatest && isCurrent && <span style={{ background: "#16a34a", color: "#fff", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>✓ Current Year</span>}
                            {isLatest && !isCurrent && <span style={{ background: "#E7730A", color: "#fff", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>Latest Available</span>}
                            {!isLatest && <span style={{ background: "#6b7280", color: "#fff", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>Archive</span>}
                        </div>
                        <div style={{ fontSize: 12, color: "#475569" }}>
                            Payroll will use: <strong>{maxYear ?? "—"}</strong> brackets (most recent available)
                            {isMissing && <span style={{ marginLeft: 8, color: "#dc2626", fontWeight: 600 }}>⚠️ {currentYear} brackets missing — add them before running payroll</span>}
                        </div>
                        <div style={{ marginLeft: "auto", fontSize: 11, color: "#94a3b8" }}>
                            Available years: {years.join(", ")}
                        </div>
                    </div>
                );
            })()}

            {/* Copy Year Panel */}
            <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 8, padding: 16, marginBottom: 24 }}>
                <p style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: "#0369a1" }}>Copy Brackets to New Year</p>
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                    <div>
                        <label style={{ fontSize: 12, fontWeight: 600, marginRight: 6 }}>From Year:</label>
                        <select value={copyFrom ?? ""} onChange={e => setCopyFrom(Number(e.target.value))} style={{ ...inp, padding: "5px 8px", minWidth: 80 }}>
                            {years.length === 0 && <option value="">Loading…</option>}
                            {years.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>
                    <span style={{ fontSize: 18, color: "#94a3b8" }}>→</span>
                    <div>
                        <label style={{ fontSize: 12, fontWeight: 600, marginRight: 6 }}>To Year:</label>
                        <input type="number" value={copyTo} onChange={e => setCopyTo(e.target.value)} style={{ ...inp, width: 80, padding: "5px 8px" }} />
                    </div>
                    <button onClick={() => copyMut.mutate()} disabled={!copyFrom || copyMut.isPending}
                        style={{ ...btn, background: "#0369a1", color: "#fff", padding: "6px 16px" }}>
                        {copyMut.isPending ? "Copying…" : "Copy Year"}
                    </button>
                    {copyMsg && <span style={{ fontSize: 12, color: copyMsg.includes("Copied") ? "#16a34a" : "#dc2626" }}>{copyMsg}</span>}
                </div>
            </div>

            {/* Filter Bar */}
            <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
                <div>
                    <label style={{ fontSize: 12, fontWeight: 600, marginRight: 6 }}>Year:</label>
                    <select value={year ?? ""} onChange={e => setYear(Number(e.target.value))} style={{ ...inp, padding: "6px 10px", minWidth: 80 }}>
                        {years.length === 0 && <option value="">Loading…</option>}
                        {years.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>
                <button onClick={() => { setAddRow(true); setNewRow({ year, jurisdiction, taxType, filingStatus: "S", minWage: 0, maxWage: null, baseAmount: 0, rate: 0, excessOver: 0 }); }}
                    style={{ ...btn, background: "#E7730A", color: "#fff", padding: "6px 14px", marginLeft: "auto" }}>
                    + Add Bracket
                </button>
            </div>

            {isLoading && <p style={{ color: "#888" }}>Loading…</p>}

            {!isLoading && (
                <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                        <thead>
                            <tr style={{ background: "#1a3a5c", color: "#fff" }}>
                                {["ID", "Filing Status", "Min Wage", "Max Wage", "Base Amount", "Rate", "Excess Over", "Actions"].map(h => (
                                    <th key={h} style={{ padding: "10px 10px", textAlign: "left", fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {addRow && (
                                <tr style={{ background: "#f0fdf4" }}>
                                    <td style={{ padding: "4px 8px", color: "#aaa", fontSize: 11 }}>new</td>
                                    <td><select value={newRow.filingStatus} onChange={e => setNewRow(r => ({ ...r, filingStatus: e.target.value }))} style={inp}><option>S</option><option>MFJ</option><option>MFS</option><option>HOH</option><option>ALL</option></select></td>
                                    <td><input value={newRow.minWage} onChange={e => setNewRow(r => ({ ...r, minWage: e.target.value }))} style={{ ...inp, width: 90 }} /></td>
                                    <td><input value={newRow.maxWage ?? ""} onChange={e => setNewRow(r => ({ ...r, maxWage: e.target.value || null }))} style={{ ...inp, width: 90 }} placeholder="∞" /></td>
                                    <td><input value={newRow.baseAmount} onChange={e => setNewRow(r => ({ ...r, baseAmount: e.target.value }))} style={{ ...inp, width: 90 }} /></td>
                                    <td><input value={newRow.rate} onChange={e => setNewRow(r => ({ ...r, rate: e.target.value }))} style={{ ...inp, width: 90 }} placeholder="0.05" /></td>
                                    <td><input value={newRow.excessOver} onChange={e => setNewRow(r => ({ ...r, excessOver: e.target.value }))} style={{ ...inp, width: 90 }} /></td>
                                    <td>
                                        <button onClick={handleCreate} style={{ ...btn, background: "#16a34a", color: "#fff", marginRight: 4 }}>Add</button>
                                        <button onClick={() => setAddRow(false)} style={{ ...btn, background: "#6b7280", color: "#fff" }}>Cancel</button>
                                    </td>
                                </tr>
                            )}
                            {brackets.length === 0 && !addRow && (
                                <tr><td colSpan={8} style={{ padding: 20, textAlign: "center", color: "#aaa" }}>No brackets found for {jurisdiction} / {year}.</td></tr>
                            )}
                            {brackets.map((b, i) =>
                                editId === b.id ? (
                                    <EditRow key={b.id} bracket={b} onSave={form => handleSave(b.id, form)} onCancel={() => setEditId(null)} />
                                ) : (
                                    <tr key={b.id} style={{ background: i % 2 === 0 ? "#fff" : "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                                        <td style={{ padding: "8px 10px", fontFamily: "monospace", fontSize: 11, color: "#9ca3af" }}>{b.id}</td>
                                        <td style={{ padding: "8px 10px", fontWeight: 600 }}>{b.filingStatus}</td>
                                        <td style={{ padding: "8px 10px" }}>{fmtAmt(b.minWage)}</td>
                                        <td style={{ padding: "8px 10px" }}>{b.maxWage != null ? fmtAmt(b.maxWage) : <span style={{ color: "#aaa" }}>No limit</span>}</td>
                                        <td style={{ padding: "8px 10px" }}>{fmtAmt(b.baseAmount)}</td>
                                        <td style={{ padding: "8px 10px", fontWeight: 700, color: "#1a3a5c" }}>{fmtRate(b.rate)}</td>
                                        <td style={{ padding: "8px 10px" }}>{fmtAmt(b.excessOver)}</td>
                                        <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>
                                            <button onClick={() => setEditId(b.id)} style={{ ...btn, background: "#e0f2fe", color: "#0369a1", marginRight: 4 }}>Edit</button>
                                            <button onClick={() => setConfirmDeleteId(b.id)} style={{ ...btn, background: "#fee2e2", color: "#dc2626" }}>Del</button>
                                        </td>
                                    </tr>
                                )
                            )}
                        </tbody>
                    </table>
                    {brackets.length > 0 && <p style={{ fontSize: 11, color: "#aaa", marginTop: 6 }}>{brackets.length} bracket(s) for {jurisdiction} / {year}</p>}
                </div>
            )}

            <ConfirmModal
                show={!!confirmDeleteId}
                title="Delete Tax Bracket?"
                message="This bracket will be permanently removed and cannot be recovered."
                confirmText="Delete"
                onConfirm={() => { deleteMut.mutate(confirmDeleteId); setConfirmDeleteId(null); }}
                onCancel={() => setConfirmDeleteId(null)}
            />
        </div>
    );
};

const StateBracketTable = () => {
    const [state, setState] = useState("CA");
    const qc = useQueryClient();
    return (
        <div>
            <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 20 }}>
                <label style={{ fontSize: 12, fontWeight: 600 }}>State:</label>
                <select value={state} onChange={e => { setState(e.target.value); qc.invalidateQueries({ queryKey: ["tb"] }); }}
                    style={{ ...inp, padding: "6px 10px", minWidth: 80 }}>
                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <span style={{ fontSize: 12, color: "#6b7280" }}>Select a state to view and edit its income tax brackets.</span>
            </div>
            <BracketTable key={state} jurisdiction={state} taxType="SIT" />
        </div>
    );
};

const reciprocityApi = {
    getAll:  ()          => axiosInstance.get(`/admin/reciprocity`).then(r => r.data),
    create:  (body)      => axiosInstance.post(`/admin/reciprocity`, body).then(r => r.data),
    update:  (id, body)  => axiosInstance.put(`/admin/reciprocity/${id}`, body).then(r => r.data),
    remove:  (id)        => axiosInstance.delete(`/admin/reciprocity/${id}`),
};

const settingsApi = {
    getAll:  (key, year) => axiosInstance.get(`/admin/tax-settings?settingKey=${key}&year=${year}`).then(r => r.data),
    update:  (id, body)  => axiosInstance.put(`/admin/tax-settings/${id}`, body).then(r => r.data),
    create:  (body)      => axiosInstance.post(`/admin/tax-settings`, body).then(r => r.data),
    remove:  (id)        => axiosInstance.delete(`/admin/tax-settings/${id}`),
};

const SETTING_KEYS = [
    { key: "SS_WAGE_BASE",    label: "Social Security Wage Base", hasState: false, format: "dollar", hint: "Update each November when IRS publishes. Applies to all employees." },
    { key: "FUTA_RATE",       label: "FUTA Net Rate",             hasState: true,  format: "rate",   hint: "Standard is 0.006 (0.6%). Add state-specific rows for credit reduction states (DOL publishes in November)." },
    { key: "SUTA_WAGE_BASE",  label: "SUTA Wage Base per State",  hasState: true,  format: "dollar", hint: "Use 'DEFAULT' for the fallback. Per-company rate is set in Company Settings." },
    { key: "STATE_ALLOWANCE", label: "State W-4 Allowance Amount",hasState: true,  format: "dollar", hint: "Annual allowance amount per exemption claimed on state W-4. Use 'DEFAULT' as fallback." },
];

const TaxSettingsTable = ({ settingKey, label, hasState, format, hint, year }) => {
    const [editId, setEditId] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [addRow, setAddRow] = useState(false);
    const [newRow, setNewRow] = useState({ settingKey, state: hasState ? "" : null, year, value: 0, notes: "" });
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);

    const { data: rows = [], isLoading, refetch } = useQuery({
        queryKey: ["tax-settings", settingKey, year],
        queryFn: () => settingsApi.getAll(settingKey, year),
    });

    const updateMut = useMutation({ mutationFn: ({ id, body }) => settingsApi.update(id, body), onSuccess: () => { setEditId(null); refetch(); } });
    const createMut = useMutation({ mutationFn: settingsApi.create, onSuccess: () => { setAddRow(false); refetch(); } });
    const deleteMut = useMutation({ mutationFn: settingsApi.remove, onSuccess: refetch });

    const fmtVal = v => format === "rate" ? (Number(v) * 100).toFixed(4) + "%" : "$" + Number(v).toLocaleString();

    const cols = hasState ? ["State", "Value", "Notes", "Actions"] : ["Value", "Notes", "Actions"];

    return (
        <div style={{ marginBottom: 32 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                <div>
                    <h4 style={{ fontSize: 13, fontWeight: 700, margin: 0, color: "#1a3a5c" }}>{label}</h4>
                    <p style={{ fontSize: 11, color: "#6b7280", margin: "2px 0 0" }}>{hint}</p>
                </div>
                <button onClick={() => { setAddRow(true); setNewRow({ settingKey, state: hasState ? "" : null, year, value: 0, notes: "" }); }}
                    style={{ ...btn, background: "#E7730A", color: "#fff", padding: "5px 12px", whiteSpace: "nowrap" }}>
                    + Add Row
                </button>
            </div>
            {isLoading && <p style={{ fontSize: 12, color: "#aaa" }}>Loading…</p>}
            <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                        <tr style={{ background: "#1a3a5c", color: "#fff" }}>
                            {cols.map(h => <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600 }}>{h}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {addRow && (
                            <tr style={{ background: "#f0fdf4" }}>
                                {hasState && <td style={{ padding: "4px 8px" }}><input value={newRow.state ?? ""} onChange={e => setNewRow(r => ({ ...r, state: e.target.value.toUpperCase() }))} style={{ ...inp, width: 80 }} placeholder="CA or DEFAULT" /></td>}
                                <td style={{ padding: "4px 8px" }}><input value={newRow.value} onChange={e => setNewRow(r => ({ ...r, value: e.target.value }))} style={{ ...inp, width: 110 }} placeholder={format === "rate" ? "0.006" : "176100"} /></td>
                                <td style={{ padding: "4px 8px" }}><input value={newRow.notes} onChange={e => setNewRow(r => ({ ...r, notes: e.target.value }))} style={{ ...inp, width: 180 }} placeholder="Optional note" /></td>
                                <td style={{ padding: "4px 8px" }}>
                                    <button onClick={() => createMut.mutate({ ...newRow, value: Number(newRow.value) })} style={{ ...btn, background: "#16a34a", color: "#fff", marginRight: 4 }}>Add</button>
                                    <button onClick={() => setAddRow(false)} style={{ ...btn, background: "#6b7280", color: "#fff" }}>Cancel</button>
                                </td>
                            </tr>
                        )}
                        {rows.length === 0 && !addRow && (
                            <tr><td colSpan={cols.length} style={{ padding: 16, textAlign: "center", color: "#aaa" }}>No rows for {year}.</td></tr>
                        )}
                        {rows.map((r, i) => editId === r.id ? (
                            <tr key={r.id} style={{ background: "#fffbeb" }}>
                                {hasState && <td style={{ padding: "4px 8px" }}><input value={editForm.state ?? ""} onChange={e => setEditForm(f => ({ ...f, state: e.target.value.toUpperCase() }))} style={{ ...inp, width: 80 }} /></td>}
                                <td style={{ padding: "4px 8px" }}><input value={editForm.value ?? ""} onChange={e => setEditForm(f => ({ ...f, value: e.target.value }))} style={{ ...inp, width: 110 }} /></td>
                                <td style={{ padding: "4px 8px" }}><input value={editForm.notes ?? ""} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} style={{ ...inp, width: 180 }} /></td>
                                <td style={{ padding: "4px 8px" }}>
                                    <button onClick={() => updateMut.mutate({ id: r.id, body: { ...editForm, value: Number(editForm.value) } })} style={{ ...btn, background: "#16a34a", color: "#fff", marginRight: 4 }}>Save</button>
                                    <button onClick={() => setEditId(null)} style={{ ...btn, background: "#6b7280", color: "#fff" }}>Cancel</button>
                                </td>
                            </tr>
                        ) : (
                            <tr key={r.id} style={{ background: i % 2 === 0 ? "#fff" : "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                                {hasState && <td style={{ padding: "8px 12px", fontWeight: 600, fontFamily: "monospace" }}>{r.state || <span style={{ color: "#aaa" }}>federal</span>}</td>}
                                <td style={{ padding: "8px 12px", fontWeight: 700, color: "#1a3a5c" }}>{fmtVal(r.value)}</td>
                                <td style={{ padding: "8px 12px", color: "#6b7280", fontSize: 11 }}>{r.notes || "—"}</td>
                                <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                                    <button onClick={() => { setEditId(r.id); setEditForm({ ...r }); }} style={{ ...btn, background: "#e0f2fe", color: "#0369a1", marginRight: 4 }}>Edit</button>
                                    <button onClick={() => setConfirmDeleteId(r.id)} style={{ ...btn, background: "#fee2e2", color: "#dc2626" }}>Del</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <ConfirmModal show={!!confirmDeleteId} title="Delete Tax Setting?" message="This setting will be removed. Payroll will fall back to hardcoded defaults."
                confirmText="Delete" onConfirm={() => { deleteMut.mutate(confirmDeleteId); setConfirmDeleteId(null); }} onCancel={() => setConfirmDeleteId(null)} />
        </div>
    );
};

const SDI_STATES = ["CA","CO","CT","HI","MA","NJ","NY","OR","RI","WA"];
const LOCAL_STATES = ["IN","KY","MD","MI","MO","NY","OH","PA"];

const SdiRatesTable = () => {
    const qc = useQueryClient();
    const [year, setYear] = useState(new Date().getFullYear());
    const [editId, setEditId] = useState(null);
    const [addRow, setAddRow] = useState(false);
    const [newRow, setNewRow] = useState({ jurisdiction: "CA", taxType: "SDI", rate: 0, wageBase: null, year });
    const [editForm, setEditForm] = useState({});
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);

    const { data: rows = [], isLoading, refetch } = useQuery({
        queryKey: ["sdi-rates", year],
        queryFn: () => sdiApi.getAll(year),
    });

    const updateMut = useMutation({ mutationFn: ({ id, body }) => sdiApi.update(id, body), onSuccess: () => { setEditId(null); refetch(); } });
    const createMut = useMutation({ mutationFn: sdiApi.create, onSuccess: () => { setAddRow(false); refetch(); } });
    const deleteMut = useMutation({ mutationFn: sdiApi.remove, onSuccess: refetch });

    const years = [2024, 2025, 2026];

    return (
        <div>
            <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
                <div>
                    <label style={{ fontSize: 12, fontWeight: 600, marginRight: 6 }}>Year:</label>
                    <select value={year} onChange={e => setYear(Number(e.target.value))} style={{ ...sel, padding: "6px 28px 6px 10px" }}>
                        {years.map(y => <option key={y}>{y}</option>)}
                    </select>
                </div>
                <button onClick={() => { setAddRow(true); setNewRow({ jurisdiction: "CA", taxType: "SDI", rate: 0, wageBase: null, year }); }}
                    style={{ ...btn, background: "#E7730A", color: "#fff", padding: "6px 14px", marginLeft: "auto" }}>
                    + Add SDI Rate
                </button>
            </div>
            {isLoading && <p style={{ color: "#888" }}>Loading…</p>}
            <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                        <tr style={{ background: "#1a3a5c", color: "#fff" }}>
                            {["State", "Rate", "Wage Base", "Year", "Actions"].map(h => (
                                <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600 }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {addRow && (
                            <tr style={{ background: "#f0fdf4" }}>
                                <td style={{ padding: "4px 8px" }}>
                                    <select value={newRow.jurisdiction} onChange={e => setNewRow(r => ({ ...r, jurisdiction: e.target.value }))} style={sel}>
                                        {SDI_STATES.map(s => <option key={s}>{s}</option>)}
                                    </select>
                                </td>
                                <td><input value={newRow.rate} onChange={e => setNewRow(r => ({ ...r, rate: e.target.value }))} style={{ ...inp, width: 90 }} placeholder="0.011" /></td>
                                <td><input value={newRow.wageBase ?? ""} onChange={e => setNewRow(r => ({ ...r, wageBase: e.target.value || null }))} style={{ ...inp, width: 110 }} placeholder="No cap" /></td>
                                <td><select value={newRow.year} onChange={e => setNewRow(r => ({ ...r, year: Number(e.target.value) }))} style={sel}>{years.map(y => <option key={y}>{y}</option>)}</select></td>
                                <td>
                                    <button onClick={() => createMut.mutate({ ...newRow, rate: Number(newRow.rate), wageBase: newRow.wageBase ? Number(newRow.wageBase) : null })}
                                        style={{ ...btn, background: "#16a34a", color: "#fff", marginRight: 4 }}>Add</button>
                                    <button onClick={() => setAddRow(false)} style={{ ...btn, background: "#6b7280", color: "#fff" }}>Cancel</button>
                                </td>
                            </tr>
                        )}
                        {rows.length === 0 && !addRow && (
                            <tr><td colSpan={5} style={{ padding: 20, textAlign: "center", color: "#aaa" }}>No SDI rates found for {year}.</td></tr>
                        )}
                        {rows.map((r, i) => editId === r.id ? (
                            <tr key={r.id} style={{ background: "#fffbeb" }}>
                                <td style={{ padding: "4px 8px" }}>
                                    <select value={editForm.jurisdiction} onChange={e => setEditForm(f => ({ ...f, jurisdiction: e.target.value }))} style={sel}>
                                        {SDI_STATES.map(s => <option key={s}>{s}</option>)}
                                    </select>
                                </td>
                                <td><input value={editForm.rate ?? ""} onChange={e => setEditForm(f => ({ ...f, rate: e.target.value }))} style={{ ...inp, width: 90 }} /></td>
                                <td><input value={editForm.wageBase ?? ""} onChange={e => setEditForm(f => ({ ...f, wageBase: e.target.value || null }))} style={{ ...inp, width: 110 }} placeholder="No cap" /></td>
                                <td><select value={editForm.year ?? year} onChange={e => setEditForm(f => ({ ...f, year: Number(e.target.value) }))} style={sel}>{years.map(y => <option key={y}>{y}</option>)}</select></td>
                                <td>
                                    <button onClick={() => updateMut.mutate({ id: r.id, body: { ...editForm, rate: Number(editForm.rate), wageBase: editForm.wageBase ? Number(editForm.wageBase) : null } })}
                                        style={{ ...btn, background: "#16a34a", color: "#fff", marginRight: 4 }}>Save</button>
                                    <button onClick={() => setEditId(null)} style={{ ...btn, background: "#6b7280", color: "#fff" }}>Cancel</button>
                                </td>
                            </tr>
                        ) : (
                            <tr key={r.id} style={{ background: i % 2 === 0 ? "#fff" : "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                                <td style={{ padding: "8px 12px", fontWeight: 700 }}>{r.jurisdiction}</td>
                                <td style={{ padding: "8px 12px", fontWeight: 700, color: "#1a3a5c" }}>{fmtRate(r.rate)}</td>
                                <td style={{ padding: "8px 12px" }}>{r.wageBase ? fmtAmt(r.wageBase) : <span style={{ color: "#aaa" }}>No cap</span>}</td>
                                <td style={{ padding: "8px 12px" }}>{r.year}</td>
                                <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                                    <button onClick={() => { setEditId(r.id); setEditForm({ ...r }); }} style={{ ...btn, background: "#e0f2fe", color: "#0369a1", marginRight: 4 }}>Edit</button>
                                    <button onClick={() => setConfirmDeleteId(r.id)} style={{ ...btn, background: "#fee2e2", color: "#dc2626" }}>Del</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <ConfirmModal show={!!confirmDeleteId} title="Delete SDI Rate?" message="This SDI rate will be permanently removed."
                confirmText="Delete" onConfirm={() => { deleteMut.mutate(confirmDeleteId); setConfirmDeleteId(null); }} onCancel={() => setConfirmDeleteId(null)} />
        </div>
    );
};

const LocalTaxRatesTable = () => {
    const [state, setLocalState] = useState("MD");
    const [year, setYear] = useState(new Date().getFullYear());
    const [editId, setEditId] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [addRow, setAddRow] = useState(false);
    const [newRow, setNewRow] = useState({ state, localityCode: "", localityName: "", rate: 0, year });
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);

    const { data: rows = [], isLoading, refetch } = useQuery({
        queryKey: ["local-rates", state, year],
        queryFn: () => localApi.getAll(state, year),
    });

    const updateMut = useMutation({ mutationFn: ({ id, body }) => localApi.update(id, body), onSuccess: () => { setEditId(null); refetch(); } });
    const createMut = useMutation({ mutationFn: localApi.create, onSuccess: () => { setAddRow(false); refetch(); } });
    const deleteMut = useMutation({ mutationFn: localApi.remove, onSuccess: refetch });

    const years = [2024, 2025, 2026];

    return (
        <div>
            <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
                <div>
                    <label style={{ fontSize: 12, fontWeight: 600, marginRight: 6 }}>State:</label>
                    <select value={state} onChange={e => { setLocalState(e.target.value); setEditId(null); setAddRow(false); }} style={{ ...sel, padding: "6px 28px 6px 10px" }}>
                        {LOCAL_STATES.map(s => <option key={s}>{s}</option>)}
                    </select>
                </div>
                <div>
                    <label style={{ fontSize: 12, fontWeight: 600, marginRight: 6 }}>Year:</label>
                    <select value={year} onChange={e => setYear(Number(e.target.value))} style={{ ...sel, padding: "6px 28px 6px 10px" }}>
                        {years.map(y => <option key={y}>{y}</option>)}
                    </select>
                </div>
                <button onClick={() => { setAddRow(true); setNewRow({ state, localityCode: "", localityName: "", rate: 0, year }); }}
                    style={{ ...btn, background: "#E7730A", color: "#fff", padding: "6px 14px", marginLeft: "auto" }}>
                    + Add Locality
                </button>
            </div>
            {isLoading && <p style={{ color: "#888" }}>Loading…</p>}
            <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                        <tr style={{ background: "#1a3a5c", color: "#fff" }}>
                            {["Locality Code", "Locality Name", "Rate", "Actions"].map(h => (
                                <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600 }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {addRow && (
                            <tr style={{ background: "#f0fdf4" }}>
                                <td style={{ padding: "4px 8px" }}><input value={newRow.localityCode} onChange={e => setNewRow(r => ({ ...r, localityCode: e.target.value.toUpperCase() }))} style={{ ...inp, width: 130 }} placeholder="MONTGOMERY" /></td>
                                <td><input value={newRow.localityName} onChange={e => setNewRow(r => ({ ...r, localityName: e.target.value }))} style={{ ...inp, width: 180 }} placeholder="Montgomery County" /></td>
                                <td><input value={newRow.rate} onChange={e => setNewRow(r => ({ ...r, rate: e.target.value }))} style={{ ...inp, width: 90 }} placeholder="0.032" /></td>
                                <td>
                                    <button onClick={() => createMut.mutate({ ...newRow, rate: Number(newRow.rate) })}
                                        style={{ ...btn, background: "#16a34a", color: "#fff", marginRight: 4 }}>Add</button>
                                    <button onClick={() => setAddRow(false)} style={{ ...btn, background: "#6b7280", color: "#fff" }}>Cancel</button>
                                </td>
                            </tr>
                        )}
                        {rows.length === 0 && !addRow && (
                            <tr><td colSpan={4} style={{ padding: 20, textAlign: "center", color: "#aaa" }}>No local tax rates found for {state} / {year}.</td></tr>
                        )}
                        {rows.map((r, i) => editId === r.id ? (
                            <tr key={r.id} style={{ background: "#fffbeb" }}>
                                <td style={{ padding: "4px 8px" }}><input value={editForm.localityCode ?? ""} onChange={e => setEditForm(f => ({ ...f, localityCode: e.target.value.toUpperCase() }))} style={{ ...inp, width: 130 }} /></td>
                                <td><input value={editForm.localityName ?? ""} onChange={e => setEditForm(f => ({ ...f, localityName: e.target.value }))} style={{ ...inp, width: 180 }} /></td>
                                <td><input value={editForm.rate ?? ""} onChange={e => setEditForm(f => ({ ...f, rate: e.target.value }))} style={{ ...inp, width: 90 }} /></td>
                                <td>
                                    <button onClick={() => updateMut.mutate({ id: r.id, body: { ...editForm, rate: Number(editForm.rate) } })}
                                        style={{ ...btn, background: "#16a34a", color: "#fff", marginRight: 4 }}>Save</button>
                                    <button onClick={() => setEditId(null)} style={{ ...btn, background: "#6b7280", color: "#fff" }}>Cancel</button>
                                </td>
                            </tr>
                        ) : (
                            <tr key={r.id} style={{ background: i % 2 === 0 ? "#fff" : "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                                <td style={{ padding: "8px 12px", fontFamily: "monospace", fontWeight: 600 }}>{r.localityCode}</td>
                                <td style={{ padding: "8px 12px" }}>{r.localityName}</td>
                                <td style={{ padding: "8px 12px", fontWeight: 700, color: "#1a3a5c" }}>{fmtRate(r.rate)}</td>
                                <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                                    <button onClick={() => { setEditId(r.id); setEditForm({ ...r }); }} style={{ ...btn, background: "#e0f2fe", color: "#0369a1", marginRight: 4 }}>Edit</button>
                                    <button onClick={() => setConfirmDeleteId(r.id)} style={{ ...btn, background: "#fee2e2", color: "#dc2626" }}>Del</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {rows.length > 0 && <p style={{ fontSize: 11, color: "#aaa", marginTop: 6 }}>{rows.length} locality rate(s) for {state} / {year}</p>}
            </div>
            <ConfirmModal show={!!confirmDeleteId} title="Delete Local Tax Rate?" message="This locality rate will be permanently removed."
                confirmText="Delete" onConfirm={() => { deleteMut.mutate(confirmDeleteId); setConfirmDeleteId(null); }} onCancel={() => setConfirmDeleteId(null)} />
        </div>
    );
};

const ReciprocityTable = () => {
    const [editId, setEditId] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [addRow, setAddRow] = useState(false);
    const [newRow, setNewRow] = useState({ residentState: "", workState: "", isActive: true, notes: "" });
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);
    const [search, setSearch] = useState("");

    const { data: rows = [], isLoading, refetch } = useQuery({
        queryKey: ["reciprocity"],
        queryFn: reciprocityApi.getAll,
    });

    const updateMut = useMutation({ mutationFn: ({ id, body }) => reciprocityApi.update(id, body), onSuccess: () => { setEditId(null); refetch(); } });
    const createMut = useMutation({ mutationFn: reciprocityApi.create, onSuccess: () => { setAddRow(false); setNewRow({ residentState: "", workState: "", isActive: true, notes: "" }); refetch(); } });
    const deleteMut = useMutation({ mutationFn: reciprocityApi.remove, onSuccess: refetch });

    const filtered = rows.filter(r =>
        !search || r.residentState?.includes(search.toUpperCase()) || r.workState?.includes(search.toUpperCase())
    );

    return (
        <div>
            <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
                <div>
                    <label style={{ fontSize: 12, fontWeight: 600, marginRight: 6 }}>Filter by State:</label>
                    <input value={search} onChange={e => setSearch(e.target.value)} placeholder="e.g. PA"
                        style={{ ...inp, width: 80, padding: "5px 8px" }} />
                </div>
                <button onClick={() => setAddRow(true)}
                    style={{ ...btn, background: "#E7730A", color: "#fff", padding: "6px 14px", marginLeft: "auto" }}>
                    + Add Pair
                </button>
            </div>
            {isLoading && <p style={{ color: "#888" }}>Loading…</p>}
            <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                        <tr style={{ background: "#1a3a5c", color: "#fff" }}>
                            {["Resident State", "Work State", "Active", "Notes", "Actions"].map(h => (
                                <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600 }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {addRow && (
                            <tr style={{ background: "#f0fdf4" }}>
                                <td style={{ padding: "4px 8px" }}>
                                    <select value={newRow.residentState} onChange={e => setNewRow(r => ({ ...r, residentState: e.target.value }))} style={sel}>
                                        <option value="">— Pick —</option>
                                        {US_STATES.map(s => <option key={s}>{s}</option>)}
                                    </select>
                                </td>
                                <td style={{ padding: "4px 8px" }}>
                                    <select value={newRow.workState} onChange={e => setNewRow(r => ({ ...r, workState: e.target.value }))} style={sel}>
                                        <option value="">— Pick —</option>
                                        {US_STATES.map(s => <option key={s}>{s}</option>)}
                                    </select>
                                </td>
                                <td style={{ padding: "4px 8px" }}>
                                    <input type="checkbox" checked={newRow.isActive} onChange={e => setNewRow(r => ({ ...r, isActive: e.target.checked }))} />
                                </td>
                                <td style={{ padding: "4px 8px" }}>
                                    <input value={newRow.notes} onChange={e => setNewRow(r => ({ ...r, notes: e.target.value }))} style={{ ...inp, width: 200 }} placeholder="Optional note" />
                                </td>
                                <td style={{ padding: "4px 8px" }}>
                                    <button onClick={() => createMut.mutate(newRow)} style={{ ...btn, background: "#16a34a", color: "#fff", marginRight: 4 }}>Add</button>
                                    <button onClick={() => setAddRow(false)} style={{ ...btn, background: "#6b7280", color: "#fff" }}>Cancel</button>
                                </td>
                            </tr>
                        )}
                        {filtered.length === 0 && !addRow && (
                            <tr><td colSpan={5} style={{ padding: 20, textAlign: "center", color: "#aaa" }}>No reciprocity pairs found.</td></tr>
                        )}
                        {filtered.map((r, i) => editId === r.id ? (
                            <tr key={r.id} style={{ background: "#fffbeb" }}>
                                <td style={{ padding: "4px 8px" }}>
                                    <select value={editForm.residentState} onChange={e => setEditForm(f => ({ ...f, residentState: e.target.value }))} style={sel}>
                                        {US_STATES.map(s => <option key={s}>{s}</option>)}
                                    </select>
                                </td>
                                <td style={{ padding: "4px 8px" }}>
                                    <select value={editForm.workState} onChange={e => setEditForm(f => ({ ...f, workState: e.target.value }))} style={sel}>
                                        {US_STATES.map(s => <option key={s}>{s}</option>)}
                                    </select>
                                </td>
                                <td style={{ padding: "4px 8px" }}>
                                    <input type="checkbox" checked={editForm.isActive} onChange={e => setEditForm(f => ({ ...f, isActive: e.target.checked }))} />
                                </td>
                                <td style={{ padding: "4px 8px" }}>
                                    <input value={editForm.notes ?? ""} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} style={{ ...inp, width: 200 }} />
                                </td>
                                <td style={{ padding: "4px 8px" }}>
                                    <button onClick={() => updateMut.mutate({ id: r.id, body: editForm })} style={{ ...btn, background: "#16a34a", color: "#fff", marginRight: 4 }}>Save</button>
                                    <button onClick={() => setEditId(null)} style={{ ...btn, background: "#6b7280", color: "#fff" }}>Cancel</button>
                                </td>
                            </tr>
                        ) : (
                            <tr key={r.id} style={{ background: i % 2 === 0 ? "#fff" : "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                                <td style={{ padding: "8px 12px", fontWeight: 700, fontFamily: "monospace" }}>{r.residentState}</td>
                                <td style={{ padding: "8px 12px", fontWeight: 700, fontFamily: "monospace" }}>{r.workState}</td>
                                <td style={{ padding: "8px 12px" }}>
                                    <span style={{ background: r.isActive ? "#dcfce7" : "#fee2e2", color: r.isActive ? "#16a34a" : "#dc2626", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>
                                        {r.isActive ? "Active" : "Inactive"}
                                    </span>
                                </td>
                                <td style={{ padding: "8px 12px", color: "#6b7280", fontSize: 11 }}>{r.notes || "—"}</td>
                                <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                                    <button onClick={() => { setEditId(r.id); setEditForm({ ...r }); }} style={{ ...btn, background: "#e0f2fe", color: "#0369a1", marginRight: 4 }}>Edit</button>
                                    <button onClick={() => setConfirmDeleteId(r.id)} style={{ ...btn, background: "#fee2e2", color: "#dc2626" }}>Del</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {filtered.length > 0 && <p style={{ fontSize: 11, color: "#aaa", marginTop: 6 }}>{filtered.length} reciprocity pair(s){search ? ` matching "${search.toUpperCase()}"` : ""}</p>}
            </div>
            <ConfirmModal show={!!confirmDeleteId} title="Delete Reciprocity Pair?"
                message="This pair will be removed. If both directions exist, delete both to fully remove the agreement."
                confirmText="Delete" onConfirm={() => { deleteMut.mutate(confirmDeleteId); setConfirmDeleteId(null); }} onCancel={() => setConfirmDeleteId(null)} />
        </div>
    );
};

const TaxBracketAdmin = () => {
    const [tab, setTab] = useState("federal");

    const tabStyle = active => ({
        padding: "12px 32px",
        fontSize: 15,
        fontWeight: active ? 700 : 500,
        border: "none",
        borderBottom: active ? "3px solid #E7730A" : "3px solid transparent",
        background: "transparent",
        color: active ? "#E7730A" : "#64748b",
        cursor: "pointer",
        transition: "all 0.15s",
        letterSpacing: 0.2,
    });

    return (
        <div style={{ padding: "24px", maxWidth: 1100 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Tax Bracket Administration</h2>
            <p style={{ fontSize: 13, color: "#666", marginBottom: 20 }}>
                View and edit IRS/state income tax brackets by year. Update these annually when IRS publishes new tables.
            </p>

            {/* Tabs */}
            <div style={{ display: "flex", borderBottom: "1px solid #e2e8f0", marginBottom: 24, flexWrap: "wrap" }}>
                <button style={tabStyle(tab === "federal")} onClick={() => setTab("federal")}>
                    US Federal (FITW)
                </button>
                <button style={tabStyle(tab === "state")} onClick={() => setTab("state")}>
                    State (SIT)
                </button>
                <button style={tabStyle(tab === "sdi-local")} onClick={() => setTab("sdi-local")}>
                    SDI &amp; Local Rates
                </button>
                <button style={tabStyle(tab === "tax-settings")} onClick={() => setTab("tax-settings")}>
                    Tax Settings
                </button>
                <button style={tabStyle(tab === "reciprocity")} onClick={() => setTab("reciprocity")}>
                    State Reciprocity
                </button>
            </div>

            {tab === "federal" && (
                <div>
                    <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 6, padding: "10px 14px", marginBottom: 20, fontSize: 12, color: "#92400e" }}>
                        <strong>Federal Income Tax Withholding (IRS Pub 15-T)</strong> — These brackets are used for all employees based on their W-4 filing status. Stored as jurisdiction <code>US</code>, type <code>FITW</code>.
                    </div>
                    <BracketTable jurisdiction="US" taxType="FITW" />
                </div>
            )}

            {tab === "state" && (
                <div>
                    <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6, padding: "10px 14px", marginBottom: 20, fontSize: 12, color: "#166534" }}>
                        <strong>State Income Tax (SIT)</strong> — Select a state to view and manage its progressive tax brackets. Stored as jurisdiction = state code, type <code>SIT</code>.
                    </div>
                    <StateBracketTable />
                </div>
            )}

            {tab === "sdi-local" && (
                <div>
                    <div style={{ background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: 6, padding: "10px 14px", marginBottom: 24, fontSize: 12, color: "#5b21b6" }}>
                        <strong>SDI &amp; Local Tax Rates</strong> — Update these annually. SDI rates change most years in CA, NJ, WA. Local rates rarely change but verify each January.
                    </div>

                    <div style={{ marginBottom: 32 }}>
                        <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 4, color: "#1a3a5c" }}>State Disability Insurance (SDI) / Paid Leave Rates</h3>
                        <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 12 }}>
                            Employee-side SDI/PFML rates by state and year. Applies to: CA, CO, CT, HI, MA, NJ, NY, OR, RI, WA.
                        </p>
                        <SdiRatesTable />
                    </div>

                    <hr style={{ borderColor: "#e5e7eb", marginBottom: 28 }} />

                    <div>
                        <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 4, color: "#1a3a5c" }}>Local / City / County Tax Rates</h3>
                        <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 12 }}>
                            Local income tax rates by state and locality code. Applies to: IN (counties), KY, MD, MI, MO, NY, OH, PA.
                        </p>
                        <LocalTaxRatesTable />
                    </div>
                </div>
            )}

            {tab === "tax-settings" && <TaxSettingsTab />}

            {tab === "reciprocity" && (
                <div>
                    <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 6, padding: "10px 14px", marginBottom: 20, fontSize: 12, color: "#0369a1" }}>
                        <strong>State Reciprocity Agreements</strong> — When a reciprocity agreement exists between an employee's resident state and work state,
                        they only pay income tax to their <em>resident</em> state. Both directions of each pair are stored (e.g. NJ→PA and PA→NJ).
                        Toggle <strong>Active</strong> to temporarily suspend an agreement without deleting it. Source: each state's Department of Revenue.
                    </div>
                    <ReciprocityTable />
                </div>
            )}
        </div>
    );
};

const TaxSettingsTab = () => {
    const [settingsYear, setSettingsYear] = useState(new Date().getFullYear());
    const years = [2024, 2025, 2026];
    return (
        <div>
            <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 6, padding: "10px 14px", marginBottom: 20, fontSize: 12, color: "#9a3412" }}>
                <strong>Annual Tax Settings</strong> — Update SS wage base each November. Update SUTA wage bases when states publish new limits. FUTA credit reduction states are published by DOL in November.
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
                <label style={{ fontSize: 12, fontWeight: 600 }}>Tax Year:</label>
                <select value={settingsYear} onChange={e => setSettingsYear(Number(e.target.value))} style={{ ...sel, padding: "6px 28px 6px 10px" }}>
                    {years.map(y => <option key={y}>{y}</option>)}
                </select>
            </div>
            {SETTING_KEYS.map(sk => (
                <TaxSettingsTable key={sk.key} settingKey={sk.key} label={sk.label} hasState={sk.hasState} format={sk.format} hint={sk.hint} year={settingsYear} />
            ))}
        </div>
    );
};

export default TaxBracketAdmin;
